import React, { useState, cloneElement } from "react";
import { Lock } from "lucide-react";
import { useUsage } from "@/hooks/useUsage";
import { UpgradeModal } from "@/components/UsageWidget";

/** Wraps a single button/element. If the named limit is exhausted, the
 *  child becomes visually locked (🔒 + disabled). Clicking opens UpgradeModal.
 *
 *  Usage:
 *    <LimitGate limitKey="regenerations" testid="regen-gate">
 *      <button className="btn-primary" onClick={handleRegen}>Перегенерувати</button>
 *    </LimitGate>
 *
 *  The child element receives an injected onClick handler that takes precedence
 *  when the limit is exhausted.
 */
export default function LimitGate({ limitKey, children, testid, reason }) {
    const { getLimit } = useUsage();
    const [showModal, setShowModal] = useState(false);
    const lim = getLimit(limitKey);
    const exhausted = lim?.isExhausted;

    if (!exhausted) {
        // Pass through unchanged when the user has remaining quota.
        return <div data-testid={testid}>{children}</div>;
    }

    const child = React.Children.only(children);
    const childProps = {
        disabled: true,
        "aria-disabled": true,
        onClick: (e) => { e.preventDefault(); e.stopPropagation(); setShowModal(true); },
        title: `Ліміт «${lim.label}» вичерпано (${lim.used}/${lim.limit}). Натисніть для апгрейду.`,
        className: `${child.props.className || ""} relative !cursor-not-allowed opacity-60`,
        "data-locked": "true",
    };

    return (
        <>
            <div className="relative inline-flex items-center" data-testid={testid}>
                {cloneElement(child, childProps)}
                <Lock
                    size={11}
                    className="absolute -top-1 -right-1 text-warning-c bg-background rounded-full p-0.5 border border-warning/40 pointer-events-none"
                    aria-hidden
                />
            </div>
            {showModal && (
                <UpgradeModal
                    currentPlan={lim?.currentPlan}
                    reason={reason || `Ви досягли ліміту «${lim.label}» (${lim.used}/${lim.limit}) на тарифі ${(lim.currentPlan || "starter").toUpperCase()}. Перейдіть на вищий тариф або зачекайте до скидання.`}
                    onClose={() => setShowModal(false)}
                />
            )}
        </>
    );
}
